import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import Select from 'react-select';
import {
  ValueContainer, Menu, Option, MultiValue, SingleValue, Group, GroupHeading, DropdownIndicator, IndicatorSeparator
} from "./MultiSelectComponents";

const overriddenComponents = {
  ValueContainer, Menu, Option, MultiValue, SingleValue, Group, GroupHeading, DropdownIndicator, IndicatorSeparator
};

const customStyles = ({ menuList }) => ({
  option: (provided, state) => ({
    ...provided,
    backgroundColor: (state.isSelected && !state.isFocused) ? "transparent" : provided.backgroundColor,
    color: (state.isSelected && state.isFocused) ? "#FFFFFF" : state.theme.colors.neutral90,
    padding: null
  }),
  menu: (provided, state) => ({
    ...provided,
    position: 'relative',
    boxShadow: null,
    marginBottom: null,
    marginTop: null
  }),
  menuList: (provided, state) => ({
    ...provided,
    paddingBottom: null,
    paddingTop: null,
    ...menuList
  }),
  groupHeading: (provided, state) => ({
    ...provided,
    paddingLeft: null,
    paddingRight: null
  }),
  group: (provided, state) => ({
    ...provided,
    paddingBottom: null,
    paddingTop: null
  })
});

const MultiSelect = ({ options, selectedOptions, onSelectionChanged, isMulti, isDisabled, showInvertBar, styles, onSearch, initialSearch, onBlur, ...props }) => {
  const [inputValue, setInputValue] = useState(initialSearch);
  const nonSelectedOptions = options.filter(x => !selectedOptions.map(o => o.value).includes(x.value));

  const formattedOptions = [
    {
      label: `Selected (${selectedOptions.length})`,
      options: selectedOptions,
      isSelectedGroup: true
    },
    {
      label: "Options",
      options: nonSelectedOptions
    }
  ];
  const showInvertButton = (selectedOptions.length !== 0) && (selectedOptions.length !== options.length);

  const selectAll  = useCallback(() => onSelectionChanged(options), [options]);
  const selectNone = useCallback(() => onSelectionChanged([]), []);
  const selectInv  = useCallback((value) => onSelectionChanged(value), []);

  const onInputChange = useCallback((newValue, { action }) => {
    if (action === "input-change") {
      setInputValue(newValue);
      onSearch(newValue);
    }
  }, []);
  const onChangeInternal = useCallback((newSelectedOptions, { action, option }) => {
    if (isMulti) {
      onSelectionChanged(newSelectedOptions);
    } else {
      onSelectionChanged([newSelectedOptions]);
    }
  }, []);

  return (
    // We put the onBlur capturing here to ensure that we are always blurring when appropriate.
    // Clicks to the custom Menu componentes (i.e. the All/None/Invert Bar) do not live in the scope of the react-select component,
    // so blurring is not properly handled. (The react-select component we use only tracks focus/blurring on the ValueContainer)
    <div style={{ width: "320px" }} onBlurCapture={onBlur}>
      <Select
        options={formattedOptions}
        isSearchable
        isClearable={false}
        isMulti={isMulti}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={overriddenComponents}
        styles={customStyles(styles)}
        controlShouldRenderValue
        value={selectedOptions}
        onSelectAll={selectAll}
        onSelectNone={selectNone}
        onSelectInv={() => selectInv(nonSelectedOptions)}
        onChange={onChangeInternal}
        menuIsOpen={true}
        backspaceRemovesValue={false}
        placeholder={null}
        inputValue={inputValue}
        onInputChange={onInputChange}
        showInvertButton={showInvertButton}
        isDisabled={isDisabled}
        showInvertBar={showInvertBar}
        {...props}
      />
    </div>
  );
};

export const multiSelectStyleProps = PropTypes.shape({
  /** style for menu list */
  menuList: PropTypes.object
});

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      label: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      label: PropTypes.string.isRequired
    }).isRequired
  ),
  onSelectionChanged: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  showInvertBar: PropTypes.bool,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  /** styles for Select */
  styles: multiSelectStyleProps,
  initialSearch: PropTypes.string,
  onBlur: PropTypes.func
};

MultiSelect.defaultProps = {
  selectedOptions: [],
  showInvertBar: true,
  isMulti: true,
  isDisabled: false,
  styles: {},
  onSearch: () => {},
  initialSearch: ""
};

export default MultiSelect;
